/**
 * InfoPage.jsx
 * ────────────
 * About / informational page. Modern redesign:
 * compact hero + sticky tabs + 4-step decision spheres + dark-green
 * glassmorphism matching-logic cards + animated intensity rings +
 * role placeholder cards + tilt-on-hover IPM pillars + crop cards +
 * expert-ranked note + CTA banner + footer.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import QuestionnaireBanner from "../../components/QuestionnaireBanner";

// ── Global styles ────────────────────────────────────────────
function GlobalStyles() {
  return (
    <style>{`
      :root {
        --green-900: #2e4d0e; --green-800: #3a5c12; --green-700: #3d6213;
        --green-600: #4B7518; --green-500: #5d8f22; --green-400: #7da72f;
        --green-300: #9BC071; --lime-500: #c4ca60; --lime-400: #D4D980;
        --lime-300:  #E8EDB4; --cream: #F6F9F2;   --cream-2: #edf5e1;
        --brown-900: #3a2800; --brown-700: #5C4B00; --brown-500: #775F46;
      }
      .ip-root, .ip-root * { box-sizing: border-box; }
      .ip-root {
        font-family: 'Nunito', system-ui, -apple-system, sans-serif;
        background: var(--cream);
        color: var(--brown-500);
        -webkit-font-smoothing: antialiased;
      }
      .ip-root a { color: inherit; text-decoration: none; }

      /* Nav */
      .bx-nav {
        background: #fff; border-bottom: 2px solid var(--lime-400);
        position: sticky; top: 0; z-index: 100;
        transition: box-shadow .35s, background .35s, backdrop-filter .35s;
      }
      .bx-nav.scrolled {
        background: rgba(255,255,255,.96); backdrop-filter: blur(12px);
        box-shadow: 0 4px 24px rgba(75,117,24,.13);
      }
      .bx-nav-inner {
        max-width: 1200px; margin: 0 auto; padding: 0 28px;
        height: 80px; display: flex; align-items: center; justify-content: space-between;
      }
      .bx-brand { display: flex; align-items: center; gap: 12px; }
      .bx-brand-mark { width: 46px; height: 46px; display: grid; place-items: center; }
      .bx-brand-text {
        font-weight: 900; font-size: 26px; color: var(--green-600);
        letter-spacing: .02em; line-height: 1;
      }
      .bx-brand-text small {
        display: block; font-size: 8.5px; letter-spacing: .42em;
        color: var(--brown-500); font-weight: 700; margin-top: 2px;
      }
      .nav-pill {
        position: relative; font-size: 16px; padding: 8px 16px; border-radius: 8px;
        font-weight: 500; color: var(--brown-500);
        transition: color .22s, background .22s;
        overflow: hidden; display: inline-flex; align-items: center; gap: 6px;
      }
      .nav-pill::after {
        content: ''; position: absolute; bottom: 4px; left: 50%;
        transform: translateX(-50%); width: 0; height: 2px;
        background: var(--green-600); border-radius: 99px;
        transition: width .28s cubic-bezier(.4,0,.2,1);
      }
      .nav-pill:hover { color: var(--green-600); background: rgba(75,117,24,.08); }
      .nav-pill:hover::after { width: 55%; }
      .nav-pill.active {
        background: linear-gradient(135deg, var(--lime-400), #cdd46e);
        color: #4a5500; font-weight: 700;
        box-shadow: 0 2px 10px rgba(212,217,128,.5);
      }
      .nav-pill.active::after { display: none; }

      /* Buttons */
      .btn-primary {
        display: inline-flex; align-items: center; gap: 10px;
        background: linear-gradient(110deg, var(--lime-400) 0%, #edf07e 35%, var(--lime-400) 55%, var(--lime-500) 100%);
        background-size: 250% auto; color: #D4D980; border: none; border-radius: 12px;
        padding: 14px 28px; font-size: 14px; font-weight: 700; cursor: pointer;
        font-family: inherit; box-shadow: 0 4px 20px rgba(212,217,128,.55);
        transition: background-position .55s ease, box-shadow .25s, transform .2s;
        letter-spacing: .015em;
      }
      .btn-primary:hover {
        background-position: right center;
        box-shadow: 0 10px 34px rgba(212,217,128,.75);
        transform: translateY(-3px) scale(1.02);
      }
      .btn-ghost {
        display: inline-flex; align-items: center; gap: 10px;
        background: rgba(255,255,255,.08); color: #fff;
        border: 1.5px solid rgba(255,255,255,.36); border-radius: 12px;
        padding: 13px 28px; font-size: 14px; font-weight: 500; cursor: pointer;
        font-family: inherit; backdrop-filter: blur(6px);
        transition: background .25s, border-color .25s, box-shadow .25s, transform .2s;
        letter-spacing: .015em;
      }
      .btn-ghost:hover {
        background: rgba(255,255,255,.17); border-color: rgba(255,255,255,.78);
        box-shadow: 0 8px 26px rgba(0,0,0,.22); transform: translateY(-3px);
      }
      .species { font-style: italic; color: var(--lime-400); font-weight: 600; }
      .species-d { font-style: italic; color: var(--green-600); font-weight: 600; }

      /* Animations */
      @keyframes ip-drift {
        0%,100% { transform: translate(0,0) scale(1); }
        33%      { transform: translate(14px,-20px) scale(1.05); }
        66%      { transform: translate(-10px,12px) scale(.96); }
      }
      .reveal { transition: opacity .8s cubic-bezier(.2,.7,.2,1), transform .8s cubic-bezier(.2,.7,.2,1); }
      .js-anim .reveal:not(.in) { opacity: 0; transform: translateY(24px); }
      .reveal.in { opacity: 1; transform: translateY(0); }

      /* Info hero */
      .info-hero {
        background: linear-gradient(135deg, var(--green-700) 0%, var(--green-600) 55%, var(--green-500) 100%);
        position: relative; overflow: hidden;
      }
      .info-hero-deco { position: absolute; inset: 0; pointer-events: none; }
      .info-hero-deco svg { position: absolute; }
      .info-hero-deco .rings-tr { top: -20px; right: -40px; opacity: .14; }
      .info-hero-deco .rings-bl { bottom: 40px; left: -60px; opacity: .08; }
      .info-hero-deco .orb { position: absolute; border-radius: 50%; filter: blur(60px); }
      .info-hero-inner {
        max-width: 1200px; margin: 0 auto; padding: 48px 28px 0;
        position: relative; z-index: 2;
      }
      .breadcrumb {
        display: inline-flex; align-items: center; gap: 8px;
        font-size: 12px; color: rgba(255,255,255,.6); margin-bottom: 22px;
      }
      .breadcrumb a { color: rgba(255,255,255,.6); transition: color .2s; }
      .breadcrumb a:hover { color: var(--lime-400); }
      .breadcrumb .here { color: var(--lime-400); }
      .info-hero h1 {
        font-size: clamp(30px, 4vw, 46px); font-weight: 900; color: #fff;
        margin: 0 0 16px; line-height: 1.12; letter-spacing: -.015em;
      }
      .info-hero h1 .it { color: var(--lime-400); font-style: italic; font-weight: 700; }
      .info-hero .lead {
        font-size: 15.5px; color: rgba(255,255,255,.85); line-height: 1.78;
        max-width: 620px; margin: 0 0 28px;
      }
      .info-hero-ctas { display: flex; gap: 14px; flex-wrap: wrap; padding-bottom: 56px; }
      .wave-divider {
        display: block; width: 100%; height: 56px; margin-bottom: -1px;
        position: relative; z-index: 2;
      }

      /* Tabs bar */
      .tabs-bar {
        background: #fff; border-bottom: 1.5px solid #e8f0db;
        box-shadow: 0 4px 18px rgba(0,0,0,.04);
        position: sticky; top: 80px; z-index: 90;
      }
      .tabs-inner { max-width: 1200px; margin: 0 auto; padding: 0 28px; display: flex; gap: 4px; }
      .info-tab {
        padding: 14px 24px; border: none; background: transparent; cursor: pointer;
        font-family: inherit; font-size: 14px; font-weight: 600; color: var(--brown-500);
        border-bottom: 3px solid transparent;
        transition: color .2s, border-color .2s, background .2s;
        display: inline-flex; align-items: center; gap: 8px;
      }
      .info-tab:hover { color: var(--green-600); background: rgba(75,117,24,.04); }
      .info-tab.active {
        color: var(--green-600); border-bottom-color: var(--green-600); font-weight: 800;
      }

      /* Section head */
      .section-head { display: flex; align-items: center; gap: 18px; margin-bottom: 16px; }
      .head-badge {
        width: 56px; height: 56px; border-radius: 16px;
        background: linear-gradient(135deg, var(--green-600), var(--green-400));
        color: #fff; display: grid; place-items: center; flex-shrink: 0;
        box-shadow: 0 8px 22px rgba(75,117,24,.32);
      }
      .head-badge.brown {
        background: linear-gradient(135deg, var(--brown-700), var(--brown-500));
        color: var(--lime-400); box-shadow: 0 8px 22px rgba(92,75,0,.30);
      }
      .section-head h2 {
        font-size: clamp(22px, 2.6vw, 30px); font-weight: 900; color: var(--green-600);
        margin: 4px 0 0; line-height: 1.15; letter-spacing: -.01em;
      }
      .section-head h2.brown { color: var(--brown-700); }
      .eyebrow {
        font-size: 11px; font-weight: 800; text-transform: uppercase;
        letter-spacing: .14em; color: var(--green-400);
        display: inline-flex; align-items: center; gap: 10px;
      }
      .eyebrow::before {
        content: ''; width: 24px; height: 2px;
        background: var(--green-400); border-radius: 99px;
      }
      .section-lead {
        font-size: 15.5px; color: var(--brown-500); line-height: 1.75; max-width: 680px;
      }

      /* A: 4-step spheres */
      .dss-section { background: var(--cream); padding-bottom: 0; }
      .dss-A { max-width: 1200px; margin: 0 auto; padding: 64px 28px 60px; }
      .steps-intro { max-width: 720px; margin: 12px 0 52px 74px; }
      .steps-row {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
        align-items: start; position: relative; margin-bottom: 44px;
      }
      .step-col {
        display: flex; flex-direction: column; align-items: center;
        text-align: center; position: relative;
      }
      .step-sphere {
        width: 86px; height: 86px; border-radius: 50%;
        background: radial-gradient(circle at 36% 30%, #a8de4a 0%, #7db52e 22%, #4B7518 52%, #2d5010 80%, #1c3408 100%);
        box-shadow:
          inset 4px 4px 10px rgba(255,255,255,.22),
          inset -4px -4px 12px rgba(0,0,0,.38),
          0 12px 30px rgba(75,117,24,.50);
        display: grid; place-items: center; color: #fff; font-size: 30px; font-weight: 900;
        position: relative; z-index: 2;
        transition: transform .35s cubic-bezier(.2,.7,.2,1);
      }
      .step-sphere::before {
        content: ''; position: absolute; top: 14%; left: 22%;
        width: 30%; height: 15%; background: rgba(255,255,255,.42);
        border-radius: 50%; transform: rotate(-35deg); filter: blur(2px);
      }
      .step-col:hover .step-sphere { transform: translateY(-4px) scale(1.04); }
      .step-icon-badge {
        width: 38px; height: 38px; border-radius: 11px;
        background: var(--lime-300); border: 1.5px solid var(--lime-400);
        color: var(--green-600); display: grid; place-items: center;
        margin-top: -12px; z-index: 3; box-shadow: 0 2px 10px rgba(75,117,24,.18);
      }
      .step-title {
        font-size: 14px; font-weight: 800; color: var(--brown-700);
        margin-top: 12px; line-height: 1.3; min-height: 36px;
        display: flex; align-items: center; justify-content: center;
        width: 100%; text-align: center;
      }
      .step-list {
        list-style: none; margin: 8px 0 0; padding: 0;
        text-align: center; width: 100%; box-sizing: border-box;
      }
      .step-list li { font-size: 13px; color: var(--brown-500); line-height: 1.7; margin-bottom: 3px; }
      .step-connector {
        position: absolute; top: 40px; height: 3px; left: 13%; right: 13%;
        background-image: radial-gradient(circle, var(--green-300) 1.5px, transparent 2px);
        background-size: 12px 3px; opacity: .6;
      }
      .result-ribbon {
        background: linear-gradient(135deg, var(--green-700), var(--green-600), var(--green-500));
        border-radius: 18px; padding: 22px 28px;
        display: grid; grid-template-columns: auto 1fr; align-items: center; gap: 22px;
        box-shadow: 0 10px 30px rgba(75,117,24,.32);
        position: relative; overflow: hidden;
      }
      .result-ribbon::after {
        content: ''; position: absolute; right: -40px; top: -40px;
        width: 180px; height: 180px;
        background: radial-gradient(circle, rgba(212,217,128,.20), transparent 70%);
        border-radius: 50%;
      }
      .result-ribbon .ic {
        width: 56px; height: 56px; border-radius: 16px;
        background: rgba(212,217,128,.18); border: 1px solid rgba(212,217,128,.35);
        display: grid; place-items: center; color: var(--lime-400);
      }
      .result-ribbon .rt { font-size: 16px; font-weight: 800; color: #fff; margin-bottom: 4px; }
      .result-ribbon .rb { font-size: 13.5px; color: rgba(255,255,255,.85); line-height: 1.65; }

      /* B: Matching logic on dark green */
      .dss-B {
        background: linear-gradient(135deg, #2e4d0e, #3d6213 40%, #4B7518 70%, #5a8020 100%);
        padding: 72px 28px; position: relative; overflow: hidden;
      }
      .dss-B .orb {
        position: absolute; border-radius: 50%; pointer-events: none; filter: blur(40px);
      }
      .dss-B-inner { max-width: 1200px; margin: 0 auto; position: relative; z-index: 2; }
      .dss-B-head { text-align: center; margin-bottom: 44px; }
      .dss-B-head .eyebrow {
        color: var(--lime-400); justify-content: center; display: inline-flex;
      }
      .dss-B-head .eyebrow::before { background: var(--lime-400); }
      .dss-B-head h2 {
        font-size: clamp(22px, 2.8vw, 32px); font-weight: 900; color: #fff;
        margin: 10px 0 12px; letter-spacing: -.01em;
      }
      .dss-B-head p {
        font-size: 15px; color: rgba(255,255,255,.82); line-height: 1.75;
        max-width: 660px; margin: 0 auto;
      }
      .glass-row { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
      .glass-card {
        background: rgba(255,255,255,.78);
        backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
        border: 1.5px solid rgba(255,255,255,.85); border-radius: 22px;
        padding: 30px 30px; box-shadow: 0 12px 36px rgba(0,0,0,.20);
        transition: transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s;
      }
      .glass-card:hover {
        transform: translateY(-6px); box-shadow: 0 22px 50px rgba(0,0,0,.28);
      }
      .glass-head { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
      .sphere-sm {
        width: 46px; height: 46px; border-radius: 50%;
        background: radial-gradient(circle at 36% 30%, #a8de4a 0%, #7db52e 22%, #4B7518 52%, #2d5010 80%, #1c3408 100%);
        box-shadow:
          inset 3px 3px 8px rgba(255,255,255,.22),
          inset -3px -3px 10px rgba(0,0,0,.38),
          0 6px 16px rgba(75,117,24,.38);
        display: grid; place-items: center; color: #fff;
      }
      .sphere-sm.amber {
        background: radial-gradient(circle at 36% 30%, #d3a55a 0%, #b87f1a 35%, #5C4B00 70%, #352a00 100%);
        box-shadow:
          inset 3px 3px 8px rgba(255,255,255,.22),
          inset -3px -3px 10px rgba(0,0,0,.38),
          0 6px 16px rgba(92,75,0,.38);
      }
      .glass-head h3 { font-size: 18px; font-weight: 800; color: var(--green-600); margin: 0; }
      .glass-head h3.brown { color: var(--brown-700); }
      .glass-card .intro {
        font-size: 14px; color: var(--brown-700); line-height: 1.75; margin: 0 0 16px;
      }
      .crit {
        display: grid; grid-template-columns: auto 1fr; gap: 12px;
        margin-bottom: 12px; align-items: flex-start;
      }
      .crit .bullet { width: 9px; height: 9px; border-radius: 50%; background: var(--green-600); margin-top: 6px; }
      .crit.brown .bullet { background: var(--brown-700); }
      .crit p { margin: 0; font-size: 13.5px; color: var(--brown-700); line-height: 1.65; }
      .crit strong { color: var(--green-700); }
      .crit.brown strong { color: var(--brown-700); }
      .callout {
        margin-top: 14px; padding: 12px 16px;
        background: rgba(75,117,24,.08); border-radius: 10px;
        border-left: 3px solid var(--green-600);
        font-size: 13px; color: var(--green-700); font-weight: 600; line-height: 1.6;
      }
      .callout.brown {
        background: rgba(92,75,0,.08); border-left-color: var(--brown-500);
        color: var(--brown-500); font-style: italic; font-weight: 500;
      }

      /* C: Intensity rings */
      .dss-C { max-width: 1200px; margin: 0 auto; padding: 64px 28px 56px; }
      .intensity-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
      .intensity {
        border-radius: 22px; padding: 32px 26px; border: 2px solid;
        transition: transform .35s cubic-bezier(.2,.7,.2,1), box-shadow .35s;
      }
      .intensity:hover { transform: translateY(-6px); }
      .intensity.low  { background: linear-gradient(135deg, #f0f8e8, #e4f5d0); border-color: var(--green-300); }
      .intensity.med  { background: linear-gradient(135deg, #e8f2d8, #d4eab8); border-color: var(--green-600); }
      .intensity.high { background: linear-gradient(135deg, #d8ecb8, #c0de90); border-color: #2d5010; }
      .intensity.low:hover  { box-shadow: 0 18px 40px rgba(155,192,113,.40); }
      .intensity.med:hover  { box-shadow: 0 18px 40px rgba(75,117,24,.36); }
      .intensity.high:hover { box-shadow: 0 18px 40px rgba(45,80,16,.36); }
      .ring-wrap { display: grid; place-items: center; margin-bottom: 18px; }
      .ring { position: relative; width: 130px; height: 130px; }
      .ring svg { transform: rotate(-90deg); display: block; position: relative; z-index: 0; }
      .ring .ring-track { stroke: rgba(232,237,180,.85); }
      .ring .ring-fill { stroke-linecap: round; }
      .intensity.low  .ring-fill { stroke: var(--green-500); stroke-dasharray:  87 264; animation: r33  1.3s .4s  cubic-bezier(.4,0,.2,1) backwards; }
      .intensity.med  .ring-fill { stroke: var(--green-600); stroke-dasharray: 174 264; animation: r66  1.3s .55s cubic-bezier(.4,0,.2,1) backwards; }
      .intensity.high .ring-fill { stroke: #2d5010;          stroke-dasharray: 264 264; animation: r100 1.3s .7s  cubic-bezier(.4,0,.2,1) backwards; }
      @keyframes r33  { from { stroke-dasharray: 0 264; } to { stroke-dasharray:  87 264; } }
      @keyframes r66  { from { stroke-dasharray: 0 264; } to { stroke-dasharray: 174 264; } }
      @keyframes r100 { from { stroke-dasharray: 0 264; } to { stroke-dasharray: 264 264; } }
      .ring .center {
        position: absolute; inset: 0; z-index: 1;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        text-align: center; pointer-events: none;
      }
      .ring .center .lbl {
        font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase;
      }
      .ring .center .pct { font-size: 22px; font-weight: 900; line-height: 1; }
      .intensity.low  .lbl, .intensity.low  .pct { color: var(--green-500); }
      .intensity.med  .lbl, .intensity.med  .pct { color: var(--green-600); }
      .intensity.high .lbl, .intensity.high .pct { color: #2d5010; }
      .intensity .it-title {
        font-size: 18px; font-weight: 800; color: var(--green-700);
        text-align: center; margin-bottom: 8px;
      }
      .intensity .it-desc {
        font-size: 13.5px; color: var(--brown-700); text-align: center;
        line-height: 1.65; margin-bottom: 16px;
      }
      .intensity ul { margin: 0; padding-left: 18px; }
      .intensity li {
        font-size: 12.8px; color: var(--brown-700); line-height: 1.65; margin-bottom: 5px;
      }

      /* D: Roles */
      .dss-D {
        background: linear-gradient(180deg, #edf5e0, var(--cream));
        padding: 0 28px 72px;
      }
      .dss-D-inner { max-width: 1200px; margin: 0 auto; padding-top: 12px; }
      .dss-D h3 { font-size: 22px; font-weight: 900; color: var(--green-600); margin: 0 0 8px; }
      .dss-D .lead {
        font-size: 14.5px; color: var(--brown-500); margin: 0 0 28px;
        max-width: 620px; line-height: 1.72;
      }
      .role-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
      .role-card {
        border-radius: 22px; overflow: hidden; background: #fff;
        border: 1.5px solid var(--lime-400);
        transition: transform .35s, box-shadow .35s, border-color .35s;
      }
      .role-card:hover {
        transform: translateY(-6px); box-shadow: 0 18px 44px rgba(75,117,24,.22);
        border-color: var(--green-600);
      }
      .role-card .placeholder {
        aspect-ratio: 4 / 3;
        background: linear-gradient(135deg, var(--green-900), var(--green-600));
        display: grid; place-items: center; position: relative; overflow: hidden;
      }
      .role-card .placeholder img {
        position: absolute; inset: 0; width: 100%; height: 100%;
        object-fit: cover; object-position: center top;
      }
      .role-card .placeholder::before {
        content: ''; position: absolute; inset: 0; z-index: 1;
        background-image: repeating-linear-gradient(45deg, transparent 0 12px, rgba(255,255,255,.03) 12px 24px);
      }
      .role-card .placeholder svg { position: relative; z-index: 2; color: rgba(255,255,255,.85); }
      .role-card .ph-label {
        position: absolute; bottom: 12px; left: 14px;
        font-family: 'JetBrains Mono', monospace, ui-monospace;
        font-size: 9.5px; color: rgba(255,255,255,.55);
        letter-spacing: .05em; text-transform: uppercase;
      }
      .role-card .role-body { padding: 18px 20px 22px; }
      .role-card h4 { font-size: 16px; font-weight: 800; color: var(--brown-700); margin: 0 0 7px; }
      .role-card p { font-size: 13px; color: var(--brown-500); line-height: 1.68; margin: 0; }

      /* Wave divider */
      .wave-down { height: 56px; background: var(--cream); position: relative; overflow: hidden; }
      .wave-down svg { position: absolute; bottom: 0; width: 100%; height: 100%; }

      /* IPM section */
      .ipm { background: #fff; padding: 72px 28px 72px; }
      .ipm-inner { max-width: 1200px; margin: 0 auto; }
      .ipm-lead { max-width: 720px; margin: 12px 0 44px 74px; }
      .ipm-bullet-list { margin-left: 74px; }
      .ipm-lead em { color: var(--green-600); font-weight: 600; }
      .ipm-sub-h {
        font-size: 18px; font-weight: 800; color: var(--brown-700);
        margin: 0 0 20px; display: flex; align-items: center; gap: 10px;
      }
      .ipm-sub-h::before {
        content: ''; width: 22px; height: 2px;
        background: var(--green-500); border-radius: 99px;
      }
      .pillars {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-bottom: 60px;
      }
      .pillar {
        border-radius: 22px; padding: 32px 28px;
        border: 1.5px solid rgba(255,255,255,.18);
        box-shadow: 0 8px 28px rgba(0,0,0,.18);
        position: relative; overflow: hidden;
        transition: transform .12s linear, box-shadow .35s;
      }
      .pillar::before {
        content: ''; position: absolute; inset: 0;
        background: radial-gradient(circle at 30% 0%, rgba(255,255,255,.18), transparent 60%);
        pointer-events: none;
      }
      .pillar.prev { background: linear-gradient(160deg, #2e5010, #4B7518 55%, #5d8f22); }
      .pillar.mon  { background: linear-gradient(160deg, #5a7323, #7da72f 55%, #9BC071); }
      .pillar.int  { background: linear-gradient(160deg, #3a2800, #5C4B00 55%, #775F46); }
      .pillar .p-icon {
        width: 62px; height: 62px; border-radius: 50%;
        background: rgba(255,255,255,.14); border: 1.5px solid rgba(255,255,255,.30);
        display: grid; place-items: center; color: #fff; margin-bottom: 20px;
        box-shadow: inset 2px 2px 6px rgba(255,255,255,.20), inset -2px -2px 8px rgba(0,0,0,.25);
      }
      .pillar h4 { font-size: 20px; font-weight: 800; color: #fff; margin: 0 0 4px; }
      .pillar .p-sub {
        font-size: 12.5px; color: rgba(255,255,255,.72);
        font-style: italic; margin-bottom: 18px;
      }
      .pillar ul { margin: 0; padding-left: 18px; }
      .pillar li {
        font-size: 13px; color: rgba(255,255,255,.90);
        line-height: 1.7; margin-bottom: 7px;
      }

      /* Crops */
      .crops { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-bottom: 44px; }
      .crop {
        border-radius: 22px; overflow: hidden; background: #fff;
        border: 1.5px solid var(--lime-400);
        border-top: 4px solid;
        transition: transform .35s, box-shadow .35s, border-color .35s;
      }
      .crop.almond    { border-top-color: var(--green-600); }
      .crop.olive     { border-top-color: var(--green-400); }
      .crop.grapevine { border-top-color: var(--brown-700); }
      .crop:hover { transform: translateY(-7px) scale(1.01); box-shadow: 0 22px 50px rgba(75,117,24,.22); }
      .crop .crop-img { height: 200px; position: relative; overflow: hidden; }
      .crop.almond    .crop-img { background: linear-gradient(135deg, #f5d8a8, #e7b66c); }
      .crop.olive     .crop-img { background: linear-gradient(135deg, #c8d68a, #8aa654); }
      .crop.grapevine .crop-img { background: linear-gradient(135deg, #6e4f8a, #3b2557); }
      .crop .crop-img::before {
        content: ''; position: absolute; inset: 0;
        background-image: repeating-linear-gradient(45deg, transparent 0 16px, rgba(255,255,255,.10) 16px 32px);
      }
      .crop .crop-img .ph-label {
        position: absolute; bottom: 14px; left: 16px;
        font-family: 'JetBrains Mono', monospace, ui-monospace;
        font-size: 10px; letter-spacing: .08em;
        color: rgba(255,255,255,.85); text-transform: uppercase;
      }
      .crop .crop-img .glyph {
        position: absolute; top: 50%; left: 50%;
        transform: translate(-50%,-50%);
        font-size: 64px;
        filter: drop-shadow(0 6px 14px rgba(0,0,0,.25));
      }
      .crop .crop-body { padding: 20px 22px 22px; }
      .crop .crop-body h4 { font-size: 18px; font-weight: 800; color: var(--brown-700); margin: 0; }
      .crop .crop-body .sci { font-size: 12.5px; font-style: italic; font-weight: 700; margin: 2px 0 12px; }
      .crop.almond    .sci { color: var(--green-600); }
      .crop.olive     .sci { color: var(--green-400); }
      .crop.grapevine .sci { color: var(--brown-700); }
      .crop .crop-body p { font-size: 13px; color: var(--brown-500); line-height: 1.68; margin: 0; }

      /* Expert note */
      .expert-note {
        background: linear-gradient(135deg, var(--cream), var(--cream-2));
        border: 1.5px solid var(--lime-400);
        border-left: 4px solid var(--green-600);
        border-radius: 14px; padding: 22px 26px;
        display: flex; align-items: flex-start; gap: 18px;
      }
      .expert-note .star {
        width: 46px; height: 46px; border-radius: 12px;
        background: var(--lime-300); border: 1.5px solid var(--lime-400);
        color: var(--green-600); display: grid; place-items: center; flex-shrink: 0;
      }
      .expert-note h5 { font-size: 14px; font-weight: 800; color: var(--green-600); margin: 0 0 6px; }
      .expert-note p  { font-size: 13px; color: var(--brown-500); line-height: 1.72; margin: 0; }

      /* CTA banner */
      .cta-banner {
        background: linear-gradient(135deg, #2e4d0e, #4B7518 50%, #5a8020);
        padding: 64px 28px; position: relative; overflow: hidden;
      }
      .cta-banner .orb {
        position: absolute; border-radius: 50%; pointer-events: none; filter: blur(40px);
      }
      .cta-inner {
        max-width: 700px; margin: 0 auto; text-align: center;
        position: relative; z-index: 2;
      }
      .cta-inner .eyebrow {
        color: var(--lime-400); justify-content: center; display: inline-flex;
      }
      .cta-inner .eyebrow::before { background: var(--lime-400); }
      .cta-inner h2 {
        font-size: clamp(24px, 3vw, 36px); font-weight: 900; color: #fff;
        margin: 10px 0 14px; line-height: 1.15; letter-spacing: -.01em;
      }
      .cta-inner p {
        font-size: 15px; color: rgba(255,255,255,.84); line-height: 1.7; margin: 0 0 28px;
      }

      /* ── Hamburger / mobile nav ── */
      .bx-hamburger {
        display: none; background: none; border: none; cursor: pointer;
        padding: 8px; border-radius: 8px; color: var(--brown-500);
        transition: background .2s;
        align-items: center; justify-content: center; flex-shrink: 0;
      }
      .bx-hamburger:hover { background: rgba(75,117,24,.08); }
      .bx-mobile-menu {
        display: none; flex-direction: column; gap: 2px;
        background: #fff; border-top: 1px solid var(--lime-300);
        padding: 10px 16px 16px;
        box-shadow: 0 8px 24px rgba(0,0,0,.08);
      }
      .bx-mobile-menu.open { display: flex; }
      .bx-mobile-menu .nav-pill {
        width: 100%; justify-content: flex-start !important;
        padding: 12px 16px; font-size: 15px; border-radius: 10px;
      }

      /* ── Responsive ── */
      @media (max-width: 768px) {
        .bx-hamburger { display: flex !important; }
        .bx-desktop-nav { display: none !important; }
        .bx-nav-inner { height: 64px !important; padding: 0 16px !important; }
        .bx-nav-inner .bx-brand img { height: 50px !important; width: 140px !important; }

        /* Tabs bar */
        .tabs-bar { top: 64px !important; }
        .tabs-inner { overflow-x: auto; -webkit-overflow-scrolling: touch; padding: 0 16px !important; flex-wrap: nowrap !important; }
        .info-tab { padding: 12px 14px !important; font-size: 13px !important; white-space: nowrap; }

        /* Hero */
        .info-hero-inner { padding: 28px 20px 0 !important; }
        .info-hero-ctas { flex-direction: column !important; gap: 10px !important; }
        .info-hero-ctas button { width: 100% !important; justify-content: center !important; }

        /* DSS section A */
        .dss-A { padding: 36px 20px 28px !important; }
        .steps-intro { margin-left: 0 !important; max-width: 100% !important; }
        .steps-row { grid-template-columns: 1fr 1fr !important; gap: 20px 10px !important; }
        .step-connector { display: none !important; }

        /* DSS section B */
        .dss-B { padding: 40px 20px !important; }
        .glass-row { grid-template-columns: 1fr !important; }

        /* DSS section C */
        .dss-C { padding: 36px 20px 28px !important; }
        .intensity-grid { grid-template-columns: 1fr !important; }

        /* DSS section D */
        .dss-D { padding: 0 20px 48px !important; }
        .role-grid { grid-template-columns: 1fr 1fr !important; gap: 14px !important; }

        /* IPM section */
        .ipm { padding: 48px 20px !important; }
        .ipm-lead { margin-left: 0 !important; max-width: 100% !important; }
        .ipm-bullet-list { margin-left: 0 !important; grid-template-columns: 1fr !important; }
        .pillars { grid-template-columns: 1fr !important; gap: 16px !important; }
        .crops { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }

        /* Footer */
        .footer-inner { padding: 24px 20px 20px !important; }
        .footer-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
      }
      @media (max-width: 480px) {
        .steps-row { grid-template-columns: 1fr !important; }
        .role-grid { grid-template-columns: 1fr !important; }
        .crops { grid-template-columns: 1fr !important; }
        .info-hero-inner { padding: 20px 16px 0 !important; }
        .tabs-inner { padding: 0 12px !important; }
      }

      /* Footer */
      .footer { background: var(--brown-700); color: rgba(255,255,255,.75); }
      .footer-stripe { height: 4px; background: linear-gradient(90deg, var(--lime-400), var(--green-300), var(--lime-400)); }
      .footer-inner { max-width: 1200px; margin: 0 auto; padding: 36px 28px 24px; }
      .footer-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 40px; align-items: start; }
      .footer-title { font-size: 15px; font-weight: 700; color: var(--lime-400); margin-bottom: 8px; }
      .footer-sub { font-size: 13px; line-height: 1.6; }
      .footer-meta { font-size: 12px; margin-top: 10px; color: rgba(255,255,255,.5); }
      .footer-label {
        font-size: 11px; font-weight: 700; text-transform: uppercase;
        letter-spacing: .08em; color: var(--green-300); margin-bottom: 12px;
      }
      .footer-link {
        display: block; font-size: 13px; color: rgba(255,255,255,.7);
        margin-bottom: 7px; transition: color .2s, transform .2s;
      }
      .footer-link:hover { color: var(--lime-400); transform: translateX(3px); }
      /* OLD footer-eu badge style - uncomment block to revert:
      .footer-eu {
        background: #fff; border-radius: 6px; padding: 6px 10px; margin-top: 14px;
        display: inline-flex; align-items: center; gap: 10px; width: max-content;
      }
      .footer-eu .eu-flag {
        width: 36px; height: 24px; background: #003399; border-radius: 3px;
        display: grid; place-items: center; color: #FFCC00; font-size: 16px;
      }
      .footer-eu small {
        font-size: 9.5px; line-height: 1.25; color: #1a1a1a;
        font-weight: 600; text-transform: uppercase; letter-spacing: .03em;
      }
      */
      .eu-funded-img {
        display: block; margin-top: 14px; width: 160px; height: auto;
      }
      .footer-admin {
        font-size: 12px; color: rgba(255,255,255,.35);
        display: block; margin-top: 14px; transition: color .2s;
      }
      .footer-admin:hover { color: rgba(255,255,255,.6); }
      .footer-bottom {
        border-top: 1px solid rgba(212,217,128,.2);
        margin-top: 24px; padding-top: 16px;
        font-size: 11px; color: rgba(255,255,255,.4); line-height: 1.6;
      }
    `}</style>
  );
}

// ── Bexyl leaf mark ──────────────────────────────────────────
function BexylLeafmark({ size = 46 }) {
  return (
    <svg viewBox="0 0 46 46" width={size} height={size} aria-hidden="true">
      <path d="M10 40 Q22 22 30 8" stroke="#5C4B00" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <path d="M30 40 Q24 26 16 8" stroke="#5C4B00" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <ellipse cx="30" cy="10" rx="6" ry="3.5" fill="#7DA72F" transform="rotate(-30 30 10)" />
      <ellipse cx="22" cy="6"  rx="5" ry="3"   fill="#9BC071" transform="rotate(20 22 6)" />
      <ellipse cx="14" cy="9"  rx="4" ry="2.6" fill="#7DA72F" transform="rotate(-25 14 9)" />
      <ellipse cx="26" cy="14" rx="4" ry="2.6" fill="#4B7518" transform="rotate(15 26 14)" />
    </svg>
  );
}

// ── Navbar ───────────────────────────────────────────────────
function Navbar({ scrolled, active = "about" }) {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const canViewCatalog = user?.role === "risk_manager" || user?.role === "admin";

  const NavLinks = () => (
    <>
      <a href="/"       className={`nav-pill${active === "home"    ? " active" : ""}`}>Home</a>
      <a href="/search" className={`nav-pill${active === "search"  ? " active" : ""}`}>Search</a>
      <a href="/info"   className={`nav-pill${active === "about"   ? " active" : ""}`}>About</a>
      {canViewCatalog && (
        <a href="/catalog" className={`nav-pill${active === "catalog" ? " active" : ""}`}>Catalog</a>
      )}
    </>
  );

  return (
    <nav className={`bx-nav${scrolled ? " scrolled" : ""}`} data-screen-label="nav">
      <div className="bx-nav-inner">
        <a href="/" className="bx-brand">
          <img
            src="/logo.png"
            alt="BeXyl logo"
            style={{ height: 70, width: 200, objectFit: "contain" }}
            onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
          <div style={{ display: "none", alignItems: "center", gap: 8 }}>
            <div className="bx-brand-mark"><BexylLeafmark /></div>
            <div className="bx-brand-text">BEXYL<small>BEYOND XYLELLA</small></div>
          </div>
        </a>

        {/* Desktop nav links */}
        <div className="bx-desktop-nav" style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <NavLinks />
        </div>

        {/* Hamburger button — only on mobile */}
        <button
          className="bx-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      <div className={`bx-mobile-menu${menuOpen ? " open" : ""}`} role="navigation">
        <NavLinks />
      </div>
    </nav>
  );
}

// ── Info hero ────────────────────────────────────────────────
function InfoHero({ onJumpDss, onJumpIpm }) {
  return (
    <section className="info-hero" data-screen-label="hero">
      <div className="info-hero-deco" aria-hidden="true">
        <svg className="rings-tr" width="540" height="440" viewBox="0 0 540 440">
          <circle cx="430" cy="100" r="180" stroke="#D4D980" strokeWidth="1.5" fill="none" />
          <circle cx="430" cy="100" r="120" stroke="#D4D980" strokeWidth="1"   fill="none" />
          <circle cx="430" cy="100" r="70"  stroke="#D4D980" strokeWidth="1"   fill="none" />
        </svg>
        <svg className="rings-bl" width="320" height="320" viewBox="0 0 320 320">
          <circle cx="80" cy="240" r="120" stroke="#D4D980" strokeWidth="1.5" fill="none" />
          <circle cx="80" cy="240" r="64"  stroke="#9BC071" strokeWidth="1"   fill="none" />
        </svg>
        <div className="orb" style={{ width: 260, height: 260, background: "rgba(212,217,128,.18)", top: -40, right: "30%", animation: "ip-drift 14s ease-in-out infinite" }} />
      </div>

      <div className="info-hero-inner">
        <div className="breadcrumb">
          <a href="/">Home</a><span>/</span>
          <span className="here">About the DSS</span>
        </div>
        <h1>DSS and <span className="it">IPM Schemes</span></h1>
        <p className="lead">
          Understand how the BeXyl Decision Support System works, how it selects tailored IPM
          schemes, and explore the Integrated Pest Management options available for{" "}
          <span className="species">Xylella fastidiosa</span>
          {" "}management across almond, olive and grapevine crops.
        </p>
        <div className="info-hero-ctas">
          <button onClick={onJumpDss} className="btn-primary">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            Explore the DSS
          </button>
          <button onClick={onJumpIpm} className="btn-ghost">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <polyline points="9 12 10.5 13.5 13 11" />
              <polyline points="9 16 10.5 17.5 13 15" />
            </svg>
            View IPM Schemes
          </button>
        </div>
      </div>

      <svg className="wave-divider" viewBox="0 0 1200 56" preserveAspectRatio="none">
        <path d="M0,18 C200,56 360,2 560,28 C740,52 900,8 1100,24 L1200,30 L1200,56 L0,56 Z" fill="#F6F9F2" />
        <path d="M0,22 C200,54 360,6 560,30 C740,52 900,12 1100,28 L1200,32"
              fill="none" stroke="#D4D980" strokeWidth="2.5" opacity=".8" />
      </svg>
    </section>
  );
}

// ── Sticky tabs ──────────────────────────────────────────────
function StickyTabs({ activeTab, onTabClick }) {
  return (
    <div className="tabs-bar">
      <div className="tabs-inner">
        <button onClick={() => onTabClick("dss")}
                className={`info-tab${activeTab === "dss" ? " active" : ""}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          The DSS
        </button>
        <button onClick={() => onTabClick("ipm")}
                className={`info-tab${activeTab === "ipm" ? " active" : ""}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
            <rect x="9" y="3" width="6" height="4" rx="1" />
            <polyline points="9 12 10.5 13.5 13 11" />
            <polyline points="9 16 10.5 17.5 13 15" />
          </svg>
          IPM Schemes
        </button>
      </div>
    </div>
  );
}

// ── DSS section data ─────────────────────────────────────────
const DSS_STEPS = [
  { n: 1, title: "Identify your role", items: ["Farmer", "Nursery worker", "Risk manager"],
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></svg> },
  { n: 2, title: "Select crop", items: ["Almond", "Olive", "Grapevine"],
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 8C8 10 5.9 16.17 3.82 19.82A1 1 0 0 0 5 21C5 21 7 20 9 20c2 0 4-1 6-4s5-5 5-8-3-3-3 0z" /></svg> },
  { n: 3, title: "Choose objective", items: ["Manage an existing crop", "Establish/replace a plantation"],
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" fill="currentColor" /></svg> },
  { n: 4, title: "Set intensity level", items: ["Low (basic)", "Medium (standard)", "High (comprehensive)"],
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><circle cx="7" cy="9" r="2.5" fill="currentColor" /><circle cx="17" cy="15" r="2.5" fill="currentColor" /></svg> },
];

const MATCH_GENERAL = [
  // ["Crop type", "The scheme must be validated for the selected crop - almond, olive or grapevine."],
  // ["User role", "Each scheme specifies which user profiles it is relevant for."],
  // ["Management objective", "Schemes are categorised as preventive, containment or active management."],
  // ["Effort level", "Each scheme is assigned a required implementation effort (low, medium or high)."],


  ["the type of user: farmer, nursery, risk manager (phytosanitary authority, that has access all IPM schemes from farmers and nurseries)"],
  ["the crop species: almond, grapevine, olive;"],
  ["the management objective: replacement or establishment of a new plantation of the same crop species, or management of existing crops;",],
  ["the level of management intensity the actor is willing or able to implement: low, medium, high complexity.", ],

];
const MATCH_CONTEXT = [
  // ["Demarcated zone type", "Whether the area is classified as an eradication, containment or management zone affects which options are permitted."],
  // ["Disease pressure", <>The known prevalence and spread rate of <em>Xylella</em> in the area influences scheme priority.</>],
  // ["Crop phenological stage", "Certain measures are only applicable or effective at specific crop growth stages."],
  // ["Resource availability", "Labour, technical expertise and input costs affect the feasibility of higher-effort schemes."],
];

// const INTENSITY = [
//   { key: "low", cls: "low", label: "Low", pct: 33, title: "Low Intensity",
//     desc: "Schemes with fewer actions, lower operational complexity, and easier implementation. These may be more suitable when resources, labour, or technical capacity are limited.",
//     points: ["Certified, disease-free planting material", "Basic hygiene and sanitation protocols", "Awareness monitoring and symptom recognition", "Limiting movement of plant material"] },
//   { key: "med", cls: "med", label: "Medium", pct: 66, title: "Medium Intensity",
//     desc: "Combines prevention and regular monitoring. Requires moderate investment of time and resources. For buffer or containment zones.",
//     points: ["All low-effort measures", "Regular field and nursery surveys", "Vector trapping and identification", "Basic laboratory testing on suspect samples", "Coordination with local phytosanitary authorities"] },
//   { key: "high", cls: "high", label: "High", pct: 100, title: "High Intensity",
//     desc: "Comprehensive integrated approach. Requires significant technical capacity and resources. For areas with confirmed or high-risk infection.",
//     points: ["All medium-effort measures", "Intensive vector control programmes", "Molecular diagnostics and genomic surveillance", "Targeted removal of infected material", "Area-wide coordination and containment measures", "Biological and chemical vector intervention"] },
// ];

const INTENSITY = [
  { key: "low", cls: "low", label: "Low", pct: 33, title: "Low Intensity",
    desc: "Schemes with fewer actions, lower operational complexity, and easier implementation. These may be more suitable when resources, labour, or technical capacity are limited.",
    points: [] },
  { key: "med", cls: "med", label: "Medium", pct: 66, title: "Medium Intensity",
    desc: "Schemes combining a broader set of prevention, monitoring, and intervention actions. These require more planning and regular implementation but may provide stronger management support.",
    points: [] },
  { key: "high", cls: "high", label: "High", pct: 100, title: "High Intensity",
    desc: "Schemes involving more complete and demanding combinations of IPM options. These may be suitable for situations where higher investment, technical support, and management capacity are available.",
    points: [] },
];

const ROLES = [
  { title: "Farmers",
    img: "/farmer.png",
    icon: <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4" /><path d="M3 21v-1a7 7 0 0 1 14 0v1" /><path d="M17 9c2 2 4 5 4 9" /><path d="M9 4l-3-1 0 -2" /></svg>,
    desc: "To identify practical management schemes for existing orchards and vineyards, or for the establishment of new plantations." },
  { title: "Nursery Workers",
    img: "/nursery.png",
    icon: <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 20h18" /><path d="M6 20V10l3-3h6l3 3v10" /><path d="M9 20v-5h6v5" /><path d="M12 4v3" /></svg>,
    desc: "To support decisions related to plant health, prevention, monitoring, and the production of safer plant material." },
  { title: "Risk Managers",
    img: "/risk.png",
    icon: <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>,
    desc: "To explore management options across different crops, objectives, and levels of intervention, and to support communication with stakeholders." },
];

// ── DSS section ──────────────────────────────────────────────
function DssSection() {
  return (
    <section id="dss" className="dss-section" data-screen-label="dss">
      {/* A: 4-step spheres */}
      <div className="dss-A reveal">
        <div className="section-head">
          <div className="head-badge">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
          </div>
          <div>
            <div className="eyebrow">Decision Support System</div>
            <h2>How the DSS works</h2>
          </div>
        </div>
        <p className="section-lead steps-intro">
          The BeXyl DSS guides users through a structured four-step decision process. By
          answering four simple questions, you receive a personalised, expert-ranked set of
          IPM schemes.
        </p>

        <div className="steps-row">
          <div className="step-connector" />
          {DSS_STEPS.map(step => (
            <div className="step-col" key={step.n}>
              <div className="step-sphere">{step.n}</div>
              <div className="step-icon-badge">{step.icon}</div>
              <div className="step-title">{step.title}</div>
              <ul className="step-list">
                {step.items.map(it => <li key={it}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>

        <div className="result-ribbon">
          <div className="ic">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <div>
            <div className="rt">Your personalised IPM scheme set</div>
            <div className="rb">
              Based on these inputs, the DSS suggests IPM schemes that are better adapted to
              the user's situation and provides information on how each scheme was evaluated
              by experts.
            </div>
          </div>
        </div>
      </div>

      {/* B: Matching logic */}
      <div className="dss-B">
        <div className="orb" style={{ width: 340, height: 340, background: "rgba(212,217,128,.18)", top: -80, right: 60, animation: "ip-drift 9s ease-in-out infinite" }} />
        <div className="orb" style={{ width: 240, height: 240, background: "rgba(155,192,113,.18)", bottom: -60, left: 80, animation: "ip-drift 12s 2s ease-in-out infinite" }} />
        <div className="orb" style={{ width: 160, height: 160, background: "rgba(212,217,128,.14)", top: 90, left: "42%", animation: "ip-drift 7s 1s ease-in-out infinite" }} />

        <div className="dss-B-inner reveal">
          <div className="dss-B-head">
            <div className="eyebrow">Scheme matching logic</div>
            <h2>How does the DSS select suitable IPM schemes?</h2>
            <p>The DSS uses two types of information to guide the selection of IPM schemes.</p>
          </div>

          <div className="glass-row">
            <div className="glass-card">
              <div className="glass-head">
                <div className="sphere-sm">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
                  </svg>
                </div>
                <h3>General Information</h3>
              </div>
              <p className="intro">
                This information defines the main situation of the user and is used to identify the most relevant group of schemes.
                <br></br>
                The DSS considers:
              </p>
              {MATCH_GENERAL.map(([k, v]) => (
                <div className="crit" key={k}>
                  <div className="bullet" />
                  {/* <p><strong>{k}:</strong> {v}</p> */}
                  <p><strong>{k}</strong> {v}</p>
                </div>
              ))}
              <div className="callout">
                Only schemes matching <em>all these</em> criteria simultaneously are returned in the results.
              </div>
            </div>

            <div className="glass-card">
              <div className="glass-head">
                <div className="sphere-sm amber">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3 className="brown">Context-specific Information</h3>
              </div>
              <p className="intro">
                Some management actions are only suitable under certain local conditions.
                <br /><br />

                For this reason, the DSS also considers factors that may influence whether an IPM option is feasible, useful, or allowed. These may include the crop system, cultivar susceptibility, plantation age, vector pressure, infection status, and local management rules.
                <br /><br />

                For example, removing infected plants may be more relevant in young plantations with susceptible cultivars, while tolerant or resistant cultivars may remain productive even when infected. Similarly, some soil management practices may only be possible where local regulations and agronomic conditions allow them.
              </p>

              {MATCH_CONTEXT.map(([k, v]) => (
                <div className="crit brown" key={k}>
                  <div className="bullet" />
                  {/* <p><strong>{k}:</strong> {v}</p> */}
                  <p><strong>{k}</strong> {v}</p>
                </div>
              ))}
              <div className="callout brown">
                Always verify compliance with local phytosanitary regulations before implementing any scheme.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* C: Intensity rings */}
      <div className="dss-C reveal">
        <div className="section-head">
          <div className="head-badge">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="9" x2="20" y2="9" />
              <line x1="4" y1="15" x2="20" y2="15" />
              <circle cx="9" cy="9" r="2" fill="currentColor" />
              <circle cx="15" cy="15" r="2" fill="currentColor" />
            </svg>
          </div>
          <div>
            <div className="eyebrow">Implementation effort</div>
            <h2>Management Intensity Levels</h2>
          </div>
        </div>
        <p className="section-lead steps-intro">
          The DSS organizes IPM schemes according to the level of management effort required.
        </p>

        <div className="intensity-grid">
          {INTENSITY.map(lvl => (
            <div key={lvl.key} className={`intensity ${lvl.cls}`}>
              <div className="ring-wrap">
                <div className="ring">
                  <svg width="130" height="130" viewBox="0 0 130 130">
                    <circle className="ring-track" cx="65" cy="65" r="42" fill="none" strokeWidth="11" />
                    <circle className="ring-fill"  cx="65" cy="65" r="42" fill="none" strokeWidth="11" />
                  </svg>
                  <div className="center">
                    <span className="lbl">{lvl.label}</span>
                    {/* <span className="pct">{lvl.pct}%</span> */}
                  </div>
                </div>
              </div>
              <div className="it-title">{lvl.title}</div>
              <p className="it-desc">{lvl.desc}</p>
              <ul>{lvl.points.map(p => <li key={p}>{p}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>

      {/* D: Who is the DSS for */}
      <div className="dss-D reveal">
        <div className="dss-D-inner">
          <h3>Who is the DSS for?</h3>
          <p className="lead">
            This tool is designed to support different stakeholders involved in the management of <em>Xylella fastidiosa</em>.
          </p>
          <div className="role-grid">
            {ROLES.map(r => (
              <div key={r.title} className="role-card">
                <div className="placeholder">
                  {r.img ? (
                    <img src={r.img} alt={r.title} />
                  ) : (
                    <>{r.icon}<div className="ph-label">{r.title.toLowerCase()}</div></>
                  )}
                </div>
                <div className="role-body">
                  <h4>{r.title}</h4>
                  <p>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Wave divider ─────────────────────────────────────────────
function WaveDivider() {
  return (
    <div className="wave-down">
      <svg viewBox="0 0 1200 56" preserveAspectRatio="none">
        <path d="M0,0 C220,56 420,0 620,34 C820,58 970,8 1200,28 L1200,56 L0,56 Z" fill="#fff" />
        <path d="M0,4 C220,56 420,4 620,38 C820,60 970,12 1200,32"
          fill="none" stroke="#D4D980" strokeWidth="2" opacity=".6" />
      </svg>
    </div>
  );
}

// ── IPM Section data ─────────────────────────────────────────
const PILLARS = [
  { cls: "prev", title: "Prevention", sub: "Stop the spread before it starts",
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    points: ["Measures that reduce the risk of infection or help plants better tolerate disease pressure", "Examples may include the use of resistant or tolerant cultivars, certified plant material, or practices that reduce conditions favourable to disease spread."] },
  { cls: "mon", title: "Monitoring", sub: "Early detection saves crops",
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
    points: ["Regular observation or sampling to understand the presence of vectors, infected plants, or disease progression.", "Monitoring helps users decide when and where management actions are needed."] },
  { cls: "int", title: "Intervention", sub: "Contain and manage confirmed infections",
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>,
    points: ["Actions aimed at reducing vector populations, limiting disease spread, or managing infected plants and plantations."] },
];

const CROPS = [
  { cls: "almond",    name: "Almond",    sci: "Prunus dulcis",    img: "/almond.png",
    desc: <>Widely grown across Spain and southern Europe. <em>Xylella</em> causes severe leaf scorch and rapid tree decline in almond orchards.</> },
  { cls: "olive",     name: "Olive",     sci: "Olea europaea",    img: "/olive.png",
    desc: "Primary host in the Puglia (Italy) outbreak. Olive Quick Decline Syndrome (OQDS) has devastated millions of centuries-old trees." },
  { cls: "grapevine", name: "Grapevine", sci: "Vitis vinifera",   img: "/grapevine.png",
    desc: <>Susceptible to multiple <em>Xylella</em> subspecies. Pierce's Disease poses an escalating risk to European viticulture from introduced strains.</> },
];

// ── 3D tilt wrapper ──────────────────────────────────────────
function Tilt({ children, className, max = 6 }) {
  const [style, setStyle] = useState({});
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top)  / r.height - 0.5;
    setStyle({ transform: `perspective(900px) rotateX(${ny * -max}deg) rotateY(${nx * max}deg) translateY(-6px)` });
  };
  const onLeave = () => setStyle({ transform: "perspective(900px) rotateX(0) rotateY(0) translateY(0)" });
  return (
    <div className={className} style={style} onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  );
}

// ── IPM Section ──────────────────────────────────────────────
function IpmSection() {
  return (
    <section id="ipm" className="ipm" data-screen-label="ipm">
      <div className="ipm-inner reveal">

        {/* What is IPM? */}
        <div className="section-head">
          <div className="head-badge">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 8C8 10 5.9 16.17 3.82 19.82A1 1 0 0 0 5 21C5 21 7 20 9 20c2 0 4-1 6-4s5-5 5-8-3-3-3 0z" />
            </svg>
          </div>
          <div>
            <div className="eyebrow">Background</div>
            <h2>What is Integrated Pest Management?</h2>
          </div>
        </div>
        <p className="section-lead ipm-lead">
          Integrated Pest Management, or IPM, is an approach that combines different prevention, monitoring, and
          control measures to manage pests and diseases in a sustainable way.
        </p>
        <p className="section-lead ipm-lead" style={{ marginTop: "-8px" }}>
          In the case of <em style={{ color: "var(--green-600)", fontWeight: 600 }}>Xylella fastidiosa</em>, IPM does
          not rely on a single action. Instead, it combines different measures that may help to:
        </p>
        <ul className="ipm-bullet-list" style={{
          marginTop: 4, padding: 0, listStyle: "none",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 32px",
        }}>
          {[
            "reduce the spread of the bacterium;",
            "manage insect vectors;",
            "support plant health;",
            "reduce disease impact;",
            "guide timely and informed decisions.",
          ].map(item => (
            <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{
                width: 22, height: 22, borderRadius: "50%",
                background: "var(--lime-300)", border: "1.5px solid var(--green-400)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginTop: 2,
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="var(--green-600)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              <span style={{ fontSize: 15, color: "var(--brown-500)", lineHeight: 1.75 }}>{item}</span>
            </li>
          ))}
        </ul>
        <p className="section-lead ipm-lead" style={{ marginTop: 16 }}>
          The aim is to use management actions that are technically sound, economically reasonable, and as safe as
          possible for people and the environment.
        </p>

        <div style={{ height: 48 }} />

        {/* What is an IPM scheme? */}
        <div className="section-head">
          <div className="head-badge brown">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <polyline points="9 12 10.5 13.5 13 11" />
              <polyline points="9 16 10.5 17.5 13 15" />
            </svg>
          </div>
          <div>
            <div className="eyebrow">Integrated Pest Management</div>
            <h2 className="brown">What is an IPM scheme?</h2>
          </div>
        </div>
        <p className="section-lead ipm-lead">
          An IPM scheme is a structured combination of management options designed for a specific crop, user objective, and management context.
        </p>

        <h3 className="ipm-sub-h">Each scheme may include actions related to</h3>
        <div className="pillars">
          {PILLARS.map(p => (
            <Tilt key={p.title} className={`pillar ${p.cls}`} max={6}>
              <div className="p-icon">{p.icon}</div>
              <h4>{p.title}</h4>
              <div className="p-sub">{p.sub}</div>
              <ul>{p.points.map(pt => <li key={pt}>{pt}</li>)}</ul>
            </Tilt>
          ))}
        </div>

        <h3 className="ipm-sub-h">Crops Covered</h3>
        <p className="section-lead" style={{ margin: "0 0 24px", maxWidth: 660 }}>
          The DSS covers three major Mediterranean tree crops significantly affected by{" "}
          <em style={{ color: "var(--green-600)" }}>Xylella fastidiosa</em> outbreaks in Europe.
        </p>

        <div className="crops">
          {CROPS.map(c => (
            <div key={c.name} className={`crop ${c.cls}`}>
              <div className="crop-img">
                <img
                  src={c.img}
                  alt={c.name}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div className="crop-body">
                <h4>{c.name}</h4>
                <div className="sci">{c.sci}</div>
                <p>{c.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="expert-note">
          <div className="star">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div>
            <h5>Expert-ranked schemes</h5>
            <p>
              All IPM schemes in the DSS have been evaluated by IPM experts through the BeXyl project. Schemes are ordered
              so the most effective and feasible options appear first, helping users
              prioritise actions with the greatest expected impact.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── CTA Banner ───────────────────────────────────────────────
function CtaBanner({ onSearch }) {
  return (
    <section className="cta-banner">
      <div className="orb" style={{ width: 300, height: 300, background: "rgba(212,217,128,.18)", top: -100, right: 80, animation: "ip-drift 9s ease-in-out infinite" }} />
      <div className="orb" style={{ width: 200, height: 200, background: "rgba(155,192,113,.18)", bottom: -60, left: 140, animation: "ip-drift 11s 2s ease-in-out infinite" }} />
      <div className="cta-inner">
        <div className="eyebrow">Ready to get started?</div>
        <h2>Find your IPM scheme in minutes</h2>
        <p>
          Use the guided search to select your role, crop, objective and intensity level and
          receive a personalised, expert-ranked set of IPM schemes instantly.
        </p>
        <button onClick={onSearch} className="btn-primary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
          </svg>
          Start guided search
        </button>
      </div>
    </section>
  );
}

// ── Footer ───────────────────────────────────────────────────
function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="footer">
      <div className="footer-stripe" />
      <div className="footer-inner">
        <div className="footer-grid">
          <div>
            <div className="footer-title">BeXyl WP7 · IPM Decision Support System</div>
            <div className="footer-sub">EU Horizon Europe · Xylella fastidiosa management</div>
            <div className="footer-meta">© {year} BeXyl Consortium. All rights reserved.</div>
          </div>
          <div>
            <div className="footer-label">Project links</div>
            {[
              ["BeXyl project website", "https://bexylproject.org"],
              ["About BeXyl", "https://bexylproject.org/about-bexyl-project/"],
              ["EU Horizon Europe", "https://research-and-innovation.ec.europa.eu/funding/funding-opportunities/funding-programmes-and-open-calls/horizon-europe_en"],
              ["Grant Agreement No. 101060593."],
            ].map(([label, url]) => (
              <a key={label} href={url} target="_blank" rel="noreferrer" className="footer-link">{label}</a>
            ))}
            {/* OLD footer-eu badge - uncomment to revert:
            <div className="footer-eu">
              <div className="eu-flag">★</div>
              <small>Funded by<br />the European Union</small>
            </div>
            */}
            <img src="/eu1.jpg" alt="Funded by the European Union" className="eu-funded-img" />
          </div>
          <div>
            <div className="footer-label">Platform</div>
            <div className="footer-sub" style={{ color: "rgba(255,255,255,.7)", marginBottom: 6 }}>IPM DSS v1.0</div>
            <div className="footer-sub" style={{ color: "rgba(255,255,255,.7)" }}>Crops: Almond · Olive · Grapevine</div>
            <a href="/admin" className="footer-admin">Admin access</a>
            <a href="/catalog" className="footer-admin">Risk Manager</a>
          </div>
        </div>
        <div className="footer-bottom">
          This platform is part of the BeXyl project, funded by the European Union under{" "}
          <strong>Horizon Europe Programme.</strong> Grant agreement No. 101060593.
        </div>
      </div>
    </footer>
  );
}

// ── Scroll reveal hook ───────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const reduced = window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const reveals = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window) || reduced) {
      reveals.forEach(r => r.classList.add("in"));
      return;
    }
    document.documentElement.classList.add("js-anim");
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -40px 0px" });
    reveals.forEach(r => io.observe(r));
    const t = setTimeout(() => reveals.forEach(r => r.classList.add("in")), 2500);
    return () => {
      io.disconnect();
      clearTimeout(t);
      document.documentElement.classList.remove("js-anim");
    };
  }, []);
}

// ── InfoPage ─────────────────────────────────────────────────
export default function InfoPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled]   = useState(false);
  const [activeTab, setActiveTab] = useState("dss");

  useScrollReveal();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 250);
    }
    const onScroll = () => {
      setScrolled(window.scrollY > 14);
      const ipm = document.getElementById("ipm");
      if (ipm) setActiveTab(ipm.getBoundingClientRect().top <= 200 ? "ipm" : "dss");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 130;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <div className="ip-root" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <GlobalStyles />
      <Navbar scrolled={scrolled} active="about" />
      <InfoHero onJumpDss={() => navigate("/search")} onJumpIpm={() => scrollTo("ipm")} />
      <StickyTabs activeTab={activeTab} onTabClick={scrollTo} />
      <DssSection />
      <WaveDivider />
      <IpmSection />
      <CtaBanner onSearch={() => navigate("/search")} />
      <Footer />
      <QuestionnaireBanner />
    </div>
  );
}
